import { FileForUpload, UploadSource } from '@borg/types';

export function useFileUpload(options: { source: UploadSource }) {
  const isUploading = ref<boolean>(false);
  const files = ref<FileForUpload[]>([]);

  function createFileForUpload(file: File): FileForUpload {
    const clientId = file.size + new Date().getTime() + file.lastModified;
    const id = clientId;

    return {
      id,
      clientId,
      progress: 0,
      success: false,
      createdAt: new Date(),
      name: file.name,
      type: file.type,
      size: file.size,
      url: '',
      previewUrl: '',
      source: options.source,
    };
  }

  function upload(fileList: FileList) {
    const promises: Promise<void>[] = [];
    const uploadedFiles: FileForUpload[] = [];
    let start: (file: FileForUpload) => void = () => undefined;
    let done: (files: FileForUpload[]) => void = () => undefined;
    let success: (file: FileForUpload) => void = () => undefined;
    let error: (file: FileForUpload) => void = () => undefined;

    function onStart(callback: (file: FileForUpload) => void) {
      start = callback;
    }

    function onDone(callback: (files: FileForUpload[]) => void) {
      done = callback;
    }

    function onSuccess(callback: (file: FileForUpload) => void) {
      success = callback;
    }

    function onError(callback: (file: FileForUpload) => void) {
      error = callback;
    }

    function remove(file: FileForUpload) {
      files.value = files.value.filter((f) => f.clientId !== file.clientId);
    }

    for (const listedFile of Array.from(fileList)) {
      files.value.push(createFileForUpload(listedFile));

      const fileForUpload = files.value[files.value.length - 1];

      setTimeout(() => {
        start(fileForUpload);
      });

      const promise = fileUploadService
        .upload(listedFile, options.source ?? 'other', (progress) => {
          fileForUpload.progress = progress;
        })
        .then((result) => {
          if (result.isSuccess) {
            fileForUpload.id = result.data.id;
            fileForUpload.url = result.data.url;
            fileForUpload.previewUrl = result.data.previewUrl;
            fileForUpload.progress = 100;
            fileForUpload.success = true;
            success(fileForUpload);
            uploadedFiles.push(fileForUpload);
          } else {
            error(fileForUpload);
          }
        })
        .catch(() => {
          error(fileForUpload);
        })
        .finally(() => {
          remove(fileForUpload);
        });

      promises.push(promise);
    }

    isUploading.value = true;
    Promise.all(promises)
      .then(() => {
        done(uploadedFiles);
        isUploading.value = false;
      })
      .catch(() => {
        isUploading.value = false;
      });

    return {
      onStart,
      onDone,
      onSuccess,
      onError,
    };
  }

  return {
    files: readonly(files),
    isUploading: readonly(isUploading),
    upload,
  };
}
